import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { FC, useEffect, useMemo, useState } from 'react';
import { useCommsFilter, useCommunications } from '../../../../hooks/use-communications';
import { useContactState } from '../../../../hooks/use-contacts';
import { ContactApi } from '../../../../services/api-client/contact-api';
import LocationService from '../../../../services/location-service/location-service';
import { importantContacts } from '../../../../utils/contact';
import CommuncationsWidget from './communications-widget';

export interface CommunicationsSlideoutevent {
  phoneNumber?: string;
  patientId?: number;
  locationId?: number;
  timestamp?: number;
  allowReadOnly?: boolean;
  initialMessage?: string;
}

export const CommunicationsSlideout: FC<CommunicationsSlideoutevent> = (props) => {
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { showError } = useNotification();
  const { updateCommsFilter } = useCommsFilter();
  const { listenToPhoneNumbers, setPatientId: setPatientIdFilter } = useCommunications();
  const { contactCache, loadContactsByPhoneNumbers } = useContactState();
  const [patientPhoneNumbers, setPatientPhoneNumbers] = useState<string[]>(null!);
  const [initialPhoneNumber, setInitialPhoneNumber] = useState<string>(null!);
  const [patientId, setPatientId] = useState<number>(null!);
  const [timestamp, setTimestamp] = useState<number>();
  const [initialMessage] = useState<string>(props.initialMessage || '');
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    setLoading(true);

    let unlisten: any = null;
    const locationApi = LocationService.getInstance(config, oidcService);
    const contactApi = ContactApi.initialize(config, oidcService);

    (async () => {
      try {
        const location = await locationApi.getLocation(props.locationId!);
        const networkId = location?.idNetwork;

        setTimestamp(props.timestamp ? props.timestamp : null!);

        let lookupPhoneNumbers: string[] = null!;

        if (props.phoneNumber) {
          await loadContactsByPhoneNumbers([props.phoneNumber]);
          lookupPhoneNumbers = [props.phoneNumber];
        } else if (props.patientId) {
          const phoneNumbers: string[] = [];
          const patient = await contactApi.getContactDetailsById(props.patientId);

          if (patient.cellPhone && phoneNumbers.indexOf(patient.cellPhone) == -1) {
            phoneNumbers.push(patient.cellPhone);
          }

          if (patient?.homePhone && phoneNumbers.indexOf(patient.homePhone) == -1) phoneNumbers.push(patient.homePhone);
          if (patient?.workPhone && phoneNumbers.indexOf(patient.workPhone) == -1) phoneNumbers.push(patient.workPhone);

          await loadContactsByPhoneNumbers(phoneNumbers);
          lookupPhoneNumbers = phoneNumbers;
        }

        if (lookupPhoneNumbers) {
          const finalPhoneNumbers = await getPhoneNumbers(lookupPhoneNumbers, props.patientId, networkId);
          setPatientPhoneNumbers(finalPhoneNumbers);
          unlisten = listenToPhoneNumbers(finalPhoneNumbers);
          setInitialPhoneNumber(finalPhoneNumbers[0]);
        }

        if (props.patientId) {
          setPatientId(props.patientId);
          setPatientIdFilter(props.patientId);
        } else {
          setPatientIdFilter(null);
        }
      } catch (error) {
        showError('Error loading communications');
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (unlisten) unlisten();
      updateCommsFilter({ commType: undefined, deliveryType: undefined });
    };
  }, []);

  const getPhoneNumbers = useMemo(
    () => async (phoneNumbers: string[], patientId?: number, networkId?: number) => {
      const contacts = phoneNumbers
        .flatMap((phoneNumber) => contactCache[phoneNumber]?.data)
        .filter((contact) => contact);
      if (contacts && contacts.length) {
        const finalContacts = importantContacts(contacts, patientId, networkId);
        //first check patient matching id, next first responsible party in network and last first reponsible party
        const phoneNumbers: string[] = [];
        finalContacts.map((patient) => {
          if (patient?.cellPhone && phoneNumbers.indexOf(patient.cellPhone) == -1) phoneNumbers.push(patient.cellPhone);
          if (patient?.homePhone && phoneNumbers.indexOf(patient.homePhone) == -1) phoneNumbers.push(patient.homePhone);
          if (patient?.workPhone && phoneNumbers.indexOf(patient.workPhone) == -1) phoneNumbers.push(patient.workPhone);
        });
        return phoneNumbers;
      }
      return phoneNumbers;
    },
    [contactCache]
  );

  return (
    <>
      {!loading && ((patientPhoneNumbers && initialPhoneNumber) || props.allowReadOnly) && (
        <CommuncationsWidget
          patientPhoneNumbers={patientPhoneNumbers || []}
          initialPhoneNumber={initialPhoneNumber}
          locationId={props.locationId!}
          timestamp={timestamp}
          patientId={patientId}
          initialMessage={initialMessage}
        />
      )}
    </>
  );
};
